import React, { useCallback, useEffect, useRef, useState } from 'react';
import { X, Check, Volume2, Maximize2, Clock, Ban } from 'lucide-react';
import type { VideoModelType } from '../../services/api/videoGenerationService';
import { VIDEO_MODEL_CATALOG, VideoModelSpec } from './videoModelCatalog';

/**
 * 動画生成モデルを、サンプル動画を見比べながら選ぶモーダル。
 *
 * 素の <select> ではモデル名しか分からず、「Fast と Pro で何が変わるのか」を
 * 生成して課金されるまで確かめられなかった。ここで実物を見せて選ばせる。
 *
 * 読み込みコストの設計:
 * - 開いた直後に流れるのは poster 画像だけ。<video> は preload="none" なので
 *   動画本体はホバーした1本しか取りに行かない（7本並列DLを避ける）。
 * - サンプルは public/model-samples/ の静的ファイルで、ファイル名に -v1 を持つ。
 *   S3 側で immutable な Cache-Control を付けているため、2回目以降は転送ゼロで即再生される。
 */

interface VideoModelSelectModalProps {
  isOpen: boolean;
  currentModel: VideoModelType;
  onSelect: (model: VideoModelType) => void;
  onClose: () => void;
  allowedModels?: readonly VideoModelType[];
}

const COST_TIER_STYLE: Record<VideoModelSpec['costTier'], { label: string; className: string }> = {
  low: { label: '低コスト', className: 'bg-emerald-100 text-emerald-700' },
  standard: { label: '標準', className: 'bg-sky-100 text-sky-700' },
  high: { label: '高コスト', className: 'bg-amber-100 text-amber-700' },
};

const Chip: React.FC<{ icon: React.ReactNode; children: React.ReactNode }> = ({ icon, children }) => (
  <span className="inline-flex items-center gap-1 whitespace-nowrap rounded bg-gray-100 px-2 py-[3px] text-[11px] font-medium text-gray-600">
    {icon}
    {children}
  </span>
);

const VideoModelSelectModal: React.FC<VideoModelSelectModalProps> = ({
  isOpen,
  currentModel,
  onSelect,
  onClose,
  allowedModels,
}) => {
  const [selectedModel, setSelectedModel] = useState<VideoModelType>(currentModel);
  const videoRefs = useRef<Partial<Record<VideoModelType, HTMLVideoElement | null>>>({});

  // 開くたびに「今使っているモデル」へ戻す。選んで適用せずに閉じた選択を持ち越さない。
  useEffect(() => {
    if (isOpen) {
      setSelectedModel(currentModel);
    }
  }, [isOpen, currentModel]);

  const handlePreviewStart = useCallback((model: VideoModelType) => {
    const video = videoRefs.current[model];
    if (!video) return;
    // 自動再生がブラウザに拒否されても操作を止めない（ミュートなので通常は通る）
    const played = video.play();
    if (played && typeof played.catch === 'function') {
      played.catch(() => undefined);
    }
  }, []);

  const handlePreviewStop = useCallback((model: VideoModelType) => {
    const video = videoRefs.current[model];
    if (!video) return;
    video.pause();
    video.currentTime = 0;
  }, []);

  const handleConfirm = useCallback(() => {
    onSelect(selectedModel);
    onClose();
  }, [onSelect, onClose, selectedModel]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 p-6 max-[640px]:p-3"
      onClick={onClose}
    >
      <div
        className="flex max-h-[85vh] w-full max-w-[1000px] flex-col overflow-hidden rounded-2xl bg-white shadow-[0_25px_50px_-12px_rgba(0,0,0,0.25)] max-[640px]:max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex shrink-0 items-start justify-between rounded-t-lg border-b border-b-gray-200 bg-[#fafafa] px-6 py-5">
          <div className="flex flex-col gap-1">
            <h2 className="text-[18px] font-semibold text-gray-800">動画生成モデルを選ぶ</h2>
            <p className="text-[13px] text-gray-500">
              カーソルを合わせるとサンプルを再生します
            </p>
          </div>
          <button
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border-none bg-transparent text-gray-500 transition-all duration-200 ease-[ease] hover:bg-gray-100 hover:text-gray-800"
            onClick={onClose}
            aria-label="閉じる"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex min-h-0 w-full max-w-full flex-1 flex-col overflow-y-auto overflow-x-hidden p-6 [scrollbar-color:#c1c1c1_#f1f1f1] [scrollbar-width:thin] [&::-webkit-scrollbar-thumb]:rounded [&::-webkit-scrollbar-thumb]:bg-[#c1c1c1] [&::-webkit-scrollbar-track]:rounded [&::-webkit-scrollbar-track]:bg-[#f1f1f1] [&::-webkit-scrollbar]:w-2">
          <div className="grid grid-cols-[repeat(auto-fill,minmax(260px,1fr))] gap-4 max-[640px]:grid-cols-1">
            {VIDEO_MODEL_CATALOG.filter((spec) => !allowedModels || allowedModels.includes(spec.id)).map((spec) => {
              const isSelected = selectedModel === spec.id;
              const maxDuration = spec.durations[spec.durations.length - 1];
              const cost = COST_TIER_STYLE[spec.costTier];

              return (
                <button
                  key={spec.id}
                  type="button"
                  data-testid={`video-model-card-${spec.id}`}
                  aria-pressed={isSelected}
                  onClick={() => setSelectedModel(spec.id)}
                  onMouseEnter={() => handlePreviewStart(spec.id)}
                  onMouseLeave={() => handlePreviewStop(spec.id)}
                  onFocus={() => handlePreviewStart(spec.id)}
                  onBlur={() => handlePreviewStop(spec.id)}
                  className={
                    isSelected
                      ? 'flex cursor-pointer flex-col overflow-hidden rounded-xl border-2 border-violet-600 bg-gray-50 text-left shadow-[0_0_0_3px_rgba(124,58,237,0.2)] transition-all duration-200 ease-[ease] hover:-translate-y-0.5'
                      : 'flex cursor-pointer flex-col overflow-hidden rounded-xl border-2 border-gray-200 bg-gray-50 text-left transition-all duration-200 ease-[ease] hover:-translate-y-0.5 hover:border-violet-600 hover:shadow-[0_4px_12px_rgba(0,0,0,0.1)]'
                  }
                >
                  <div className="relative aspect-[16/9] w-full bg-gray-900">
                    {spec.sample ? (
                      <video
                        ref={(el) => {
                          videoRefs.current[spec.id] = el;
                        }}
                        data-testid={`video-model-sample-${spec.id}`}
                        className="h-full w-full object-cover"
                        src={spec.sample.video}
                        poster={spec.sample.poster}
                        preload="none"
                        muted
                        loop
                        playsInline
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center bg-gray-800 text-[12px] text-gray-400">
                        サンプル準備中
                      </div>
                    )}
                    {isSelected && (
                      <div className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-full bg-violet-600 text-white">
                        <Check size={16} />
                      </div>
                    )}
                    <span
                      className={`absolute bottom-2 left-2 rounded px-2 py-[3px] text-[11px] font-medium ${cost.className}`}
                    >
                      {cost.label}
                    </span>
                  </div>

                  <div className="flex flex-col p-3">
                    <h3 className="mb-1 text-[14px] font-semibold leading-[1.3] text-gray-800">{spec.label}</h3>
                    <p className="mb-2 text-[12px] leading-[1.4] text-gray-500">{spec.tagline}</p>
                    {/* 選んだあとに生成して初めて分かる制約（実人物の参照画像・英訳）をここで先に出す */}
                    <p className="mb-2 text-[11px] leading-[1.4] text-amber-700">{spec.note}</p>
                    <div className="flex flex-wrap items-center gap-1.5">
                      <Chip icon={<Clock size={11} />}>{`最大${maxDuration}秒`}</Chip>
                      <Chip icon={<Maximize2 size={11} />}>
                        {spec.supports1080p ? '1080p対応' : '720pまで'}
                      </Chip>
                      {/* 音声を出せること自体は全モデル共通。差が出るのは OFF にできるかどうか */}
                      {spec.audio === 'always' && <Chip icon={<Volume2 size={11} />}>音声OFF不可</Chip>}
                      {!spec.supportsSquare && <Chip icon={<Ban size={11} />}>1:1非対応</Chip>}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="flex shrink-0 items-center justify-end gap-3 border-t border-t-gray-200 bg-gray-50 px-6 py-4 max-[640px]:flex-col max-[640px]:items-stretch">
          <button
            className="cursor-pointer rounded-lg border border-gray-300 bg-white px-5 py-2.5 text-[14px] font-medium text-gray-500 transition-all duration-200 ease-[ease] hover:bg-gray-100 hover:text-gray-800"
            onClick={onClose}
          >
            キャンセル
          </button>
          <button
            className="inline-flex cursor-pointer items-center justify-center gap-1.5 rounded-lg border-none bg-violet-600 px-6 py-2.5 text-[14px] font-medium text-white transition-all duration-200 ease-[ease] hover:bg-violet-700"
            onClick={handleConfirm}
          >
            適用
          </button>
        </div>
      </div>
    </div>
  );
};

export default VideoModelSelectModal;
